import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { Navigate } from "@tanstack/react-router";
import {
  fetchAuthSession,
  signInWithRedirect,
  signOut,
} from "aws-amplify/auth";
import { currentPatientSession } from "../features/patient-auth";
import { initAmplify } from "./amplify";
import { isPatientSelfAuthEnabled } from "./featureFlags";
import { isPatientOnlyGroups } from "./staffGroups";

/**
 * 患者のフラグが ON のとき、切り替えの前の患者の Amplify のセッション（patient-user だけ）がスタッフの画面に来たら、
 * /forbidden で行き止まりにせず、Amplify の signOut（Cognito のログアウトを経てトップへ戻る）で消す（Issue #473）。
 */
function SignOutPatientAmplifySession() {
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void signOut().catch(() => {
      window.location.assign("/");
    });
  }, []);
  return null;
}

export function AuthGuard({ children }: PropsWithChildren) {
  initAmplify();
  const [loading, setLoading] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const authed = Boolean(session.tokens?.idToken);
        if (mounted) setIsAuthed(authed);
      } catch {
        if (mounted) setIsAuthed(false);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return null;
  if (!isAuthed) signInWithRedirect({ options: { lang: "ja" } });
  return <>{children}</>;
}

type RoleGuardProps = PropsWithChildren & {
  allowedGroups?: string[];
};

export function RoleGuard({ children, allowedGroups }: RoleGuardProps) {
  initAmplify();
  const [loading, setLoading] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);
  const [groups, setGroups] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const authed = Boolean(session.tokens?.idToken);
        const idPayload = session.tokens?.idToken
          ?.payload as Record<string, unknown> | undefined;
        const atPayload = session.tokens?.accessToken
          ?.payload as Record<string, unknown> | undefined;
        let gs = (idPayload?.["cognito:groups"] ??
          atPayload?.["cognito:groups"]) as string | string[] | undefined;
        const normGroups = Array.isArray(gs)
          ? (gs as string[])
          : gs
            ? [String(gs)]
            : [];
        if (mounted) {
          setIsAuthed(authed);
          setGroups(normGroups);
        }
      } catch {
        if (mounted) {
          setIsAuthed(false);
          setGroups([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return null;
  if (!isAuthed) {
    signInWithRedirect({ options: { lang: "ja" } });
    return null;
  }

  const isAllowed = !allowedGroups?.length
    ? true
    : groups.some((g) => allowedGroups.includes(g));
  if (!isAllowed) {
    if (isPatientSelfAuthEnabled() && isPatientOnlyGroups(groups)) {
      return <SignOutPatientAmplifySession />;
    }
    return <Navigate to="/forbidden" />;
  }

  return <>{children}</>;
}

type RoleRedirectProps = PropsWithChildren;

export function RoleRedirect({ children }: RoleRedirectProps) {
  initAmplify();
  const [loading, setLoading] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);
  const [groups, setGroups] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const authed = Boolean(session.tokens?.idToken);
        const idPayload = session.tokens?.idToken
          ?.payload as Record<string, unknown> | undefined;
        const atPayload = session.tokens?.accessToken
          ?.payload as Record<string, unknown> | undefined;
        let gs = (idPayload?.["cognito:groups"] ??
          atPayload?.["cognito:groups"]) as string | string[] | undefined;
        const normGroups = Array.isArray(gs)
          ? (gs as string[])
          : gs
            ? [String(gs)]
            : [];
        if (mounted) {
          setIsAuthed(authed);
          setGroups(normGroups);
        }
      } catch {
        if (mounted) {
          setIsAuthed(false);
          setGroups([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return null;

  const hasAny = (cands: string[]) => groups.some((g) => cands.includes(g));
  const staffTo = !isAuthed
    ? null
    : hasAny(["ops-admin", "ops-operator"])
      ? "/bizsol/dashboard"
      : hasAny(["clinic-doctor", "clinic-operator"])
        ? "/clinic/dashboard"
        : hasAny(["patient-admin", "health-nurse"])
          ? "/company-admin/dashboard"
          : null;

  // 患者のフラグが ON なら、スタッフ → SAS の患者のセッション → それ以外（Amplify の patient-user だけを含む）は Home の順
  const to = isPatientSelfAuthEnabled()
    ? (staffTo ?? (currentPatientSession() !== null ? "/patient/home" : null))
    : (staffTo ??
      (isAuthed && hasAny(["patient-user"]) ? "/patient/home" : null));

  if (to) return <Navigate to={to} />;
  return <>{children}</>;
}
